import React, { useCallback } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Fab, Paper, Typography } from '@mui/material';
import { Add, Delete, Remove } from '@mui/icons-material';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';
import { usePostChangeCartToolingQuantity } from 'services/Data/CartService';
import useImage from 'services/Hooks/useImage';

const useStyles = makeStyles(() => ({
  squareFab: {
    borderRadius: 0,
    width: 36,
    height: 36,
    minHeight: 36,
  },
}));

const CartTooling = ({ tooling, handleOpenDeleteToolingModal, index }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { getToolingImage } = useImage();

  const { mutate: changeQuantity } = usePostChangeCartToolingQuantity();

  const handleQuantityChange = useCallback(
    quantity => {
      if (quantity <= 0) {
        handleOpenDeleteToolingModal(tooling);
      } else {
        changeQuantity({ ...tooling, quantity });
      }
    },
    [changeQuantity, tooling, handleOpenDeleteToolingModal],
  );

  const imageSrc = tooling.imgSrc ? getToolingImage(tooling?.tooling_Id, tooling?.imgSrc.id) : '/images/brp-logo.png';

  const quantityControls = (
    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: 130 }}>
      <Fab
        size="small"
        color="secondary"
        className={classes.squareFab}
        disabled={tooling.quantity <= 0}
        onClick={() => handleQuantityChange(tooling.quantity - 1)}>
        <Remove />
      </Fab>
      <Typography sx={{ minWidth: 24, textAlign: 'center' }}>{tooling.quantity}</Typography>
      <Fab
        size="small"
        color="secondary"
        className={classes.squareFab}
        sx={{ color: '#000' }}
        onClick={() => handleQuantityChange(tooling.quantity + 1)}>
        <Add />
      </Fab>
    </Box>
  );

  return (
    <Paper elevation={3} sx={{ width: '100%', borderRadius: 3, overflow: 'hidden', mb: 4 }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: 130,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            width: 240,
            flexShrink: 0,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, flexShrink: 0 }}>
            <ThemedTooltip key={`item-${tooling.id}`} title={t('removeFromCart')} placement="top">
              <Fab
                color="error"
                size="small"
                className={classes.squareFab}
                onClick={() => handleOpenDeleteToolingModal(tooling)}>
                <Delete />
              </Fab>
            </ThemedTooltip>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160 }}>
            <Box
              component="img"
              src={imageSrc}
              alt={tooling?.name}
              sx={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: { md: 360, lg: 460 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: 2.5,
            py: 1.5,
          }}>
          <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.2, mb: 0.25 }}>
            {tooling?.name}
          </Typography>
          {tooling.additionalToolings?.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              {tooling.additionalToolings.map(at => (
                <Typography key={`${at?.id || at?.name}-${at?.quantity}`} variant="body2" color="text.secondary">
                  {`${at?.quantity}X ${at?.name || ''}`}
                </Typography>
              ))}
            </>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            px: 3,
            py: 1.5,
            width: 180,
            flexShrink: 0,
            ml: 'auto',
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('unitPrice')}
          </Typography>
          <Typography fontWeight={700} sx={{ fontSize: 18, whiteSpace: 'nowrap' }}>
            ${tooling?.price.toFixed(2)} {tooling.currencyName}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2.5,
            py: 1.5,
            width: 175,
            flexShrink: 0,
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          {quantityControls}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            px: 3,
            py: 1.5,
            width: 190,
            flexShrink: 0,
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('total')}
          </Typography>
          <Typography fontWeight={800} sx={{ fontSize: 22, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
            ${tooling?.subTotal.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tooling.currencyName}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          p: 2,
          gap: 1.5,
        }}>
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 0.5 }}>
          <Box
            component="img"
            src={imageSrc}
            alt={tooling?.name}
            sx={{ maxWidth: '100%', maxHeight: 170, objectFit: 'contain' }}
          />
          <ThemedTooltip title={t('removeFromCart')} placement="top">
            <Fab
              color="error"
              size="small"
              className={classes.squareFab}
              sx={{ position: 'absolute', top: 0, right: 0 }}
              onClick={() => handleOpenDeleteToolingModal(tooling)}>
              <Delete />
            </Fab>
          </ThemedTooltip>
        </Box>

        <Box>
          <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.2 }}>
            {tooling?.name}
          </Typography>
        </Box>

        {tooling.additionalToolings?.length > 0 && (
          <>
            <Divider />
            {tooling.additionalToolings.map(at => (
              <Typography key={`${at?.id || at?.name}-${at?.quantity}-m`} variant="body2" color="text.secondary">
                {`${at?.quantity}X ${at?.name || ''}`}
              </Typography>
            ))}
          </>
        )}

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('unitPrice')}
          </Typography>
          <Typography fontWeight={700} sx={{ fontSize: 18, textAlign: 'right' }}>
            ${tooling?.price.toFixed(2)} {tooling.currencyName}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('quantity')}
          </Typography>
          {quantityControls}
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', pb: 0.5 }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('price')}
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography fontWeight={800} sx={{ fontSize: 24, lineHeight: 1.1 }}>
              ${tooling?.subTotal.toFixed(2)} {tooling.currencyName}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default CartTooling;
